import React, { useState, useEffect } from 'react';
import {
  Box,
  Stack,
  Typography,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Alert,
  CircularProgress,
  Divider,
  Switch,
  FormControlLabel,
  Tabs,
  Tab
} from '@mui/material';

import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Schedule as ScheduleIcon,
  EventBusy as EventBusyIcon
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import rendezVousService, { DisponibiliteVeterinaire } from '../../services/rendezvousService';
import GestionIndisponibilites from './GestionIndisponibilites';

const GestionDisponibilites: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [disponibilites, setDisponibilites] = useState<DisponibiliteVeterinaire[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDispo, setEditingDispo] = useState<DisponibiliteVeterinaire | null>(null);
  const [planningTypeDialog, setPlanningTypeDialog] = useState(false);

  // États pour le formulaire
  const [formData, setFormData] = useState({
    jour_semaine: 0,
    heure_debut: null as Date | null,
    heure_fin: null as Date | null,
    actif: true
  });

  // États pour le planning type
  const [planningType, setPlanningType] = useState<{
    [jour: string]: { debut: string; fin: string; duree_minutes: number }[];
  }>({
    lundi: [],
    mardi: [],
    mercredi: [],
    jeudi: [],
    vendredi: [],
    samedi: [],
    dimanche: []
  });

  const joursMapping = [
    { value: 0, label: 'Lundi', key: 'lundi' },
    { value: 1, label: 'Mardi', key: 'mardi' },
    { value: 2, label: 'Mercredi', key: 'mercredi' },
    { value: 3, label: 'Jeudi', key: 'jeudi' },
    { value: 4, label: 'Vendredi', key: 'vendredi' },
    { value: 5, label: 'Samedi', key: 'samedi' },
    { value: 6, label: 'Dimanche', key: 'dimanche' }
  ];

  useEffect(() => {
    loadDisponibilites();
  }, []);

  const loadDisponibilites = async () => {
    setLoading(true);
    try {
      const data = await rendezVousService.getDisponibilites();
      setDisponibilites(data);
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des disponibilités');
      console.error('Erreur:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.heure_debut || !formData.heure_fin) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      const data = {
        jour_semaine: formData.jour_semaine,
        heure_debut: formData.heure_debut.toTimeString().slice(0, 5),
        heure_fin: formData.heure_fin.toTimeString().slice(0, 5),
        actif: formData.actif
      };

      if (editingDispo) {
        await rendezVousService.updateDisponibilite(editingDispo.id, data);
      } else {
        await rendezVousService.createDisponibilite(data);
      }

      await loadDisponibilites();
      handleCloseDialog();
    } catch (err) {
      setError('Erreur lors de la sauvegarde');
      console.error('Erreur:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette disponibilité ?')) {
      return;
    }

    try {
      await rendezVousService.deleteDisponibilite(id);
      await loadDisponibilites();
    } catch (err) {
      setError('Erreur lors de la suppression');
      console.error('Erreur:', err);
    }
  };

  const handleEdit = (dispo: DisponibiliteVeterinaire) => {
    setEditingDispo(dispo);
    setFormData({
      jour_semaine: dispo.jour_semaine,
      heure_debut: new Date(`2000-01-01T${dispo.heure_debut}`),
      heure_fin: new Date(`2000-01-01T${dispo.heure_fin}`),
      actif: dispo.actif
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingDispo(null);
    setFormData({
      jour_semaine: 0,
      heure_debut: null,
      heure_fin: null,
      actif: true
    });
  };

  const handleToggleActif = async (dispo: DisponibiliteVeterinaire) => {
    try {
      await rendezVousService.updateDisponibilite(dispo.id, {
        actif: !dispo.actif
      });
      await loadDisponibilites();
    } catch (err) {
      setError('Erreur lors de la mise à jour');
      console.error('Erreur:', err);
    }
  };

  const handlePlanningTypeChange = (
    jour: string,
    index: number,
    field: 'debut' | 'fin' | 'duree_minutes',
    value: string | number
  ) => {
    setPlanningType(prev => ({
      ...prev,
      [jour]: prev[jour].map((creneau, i) =>
        i === index ? { ...creneau, [field]: value } : creneau
      )
    }));
  };

  const addCreneauToPlanningType = (jour: string) => {
    setPlanningType(prev => ({
      ...prev,
      [jour]: [...prev[jour], { debut: '09:00', fin: '12:00', duree_minutes: 30 }]
    }));
  };

  const removeCreneauFromPlanningType = (jour: string, index: number) => {
    setPlanningType(prev => ({
      ...prev,
      [jour]: prev[jour].filter((_, i) => i !== index)
    }));
  };

  const handleSavePlanningType = async () => {
    try {
      await rendezVousService.definirPlanningType(planningType);
      await loadDisponibilites();
      setPlanningTypeDialog(false);
    } catch (err) {
      setError('Erreur lors de la sauvegarde du planning type');
      console.error('Erreur:', err);
    }
  };

  // Organiser les disponibilités par jour
  const disponibilitesParJour = joursMapping.map(jour => ({
    ...jour,
    disponibilites: disponibilites.filter(d => d.jour_semaine === jour.value)
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box p={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" color="primary">
            Zones où je prends RDV / zones où je ne prends pas RDV
          </Typography>
        </Box>

        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)} sx={{ mb: 3 }}>
          <Tab
            icon={<ScheduleIcon />}
            iconPosition="start"
            label="Créneaux disponibles (je prends RDV)"
          />
          <Tab
            icon={<EventBusyIcon />}
            iconPosition="start"
            label="Indisponibilités (je ne prends pas RDV)"
          />
        </Tabs>

        {activeTab === 0 && (
          <>
            <Box display="flex" justifyContent="flex-end" mb={2}>
              <Stack direction="row" spacing={2}>
                <Button
                  variant="outlined"
                  startIcon={<ScheduleIcon />}
                  onClick={() => setPlanningTypeDialog(true)}
                >
                  Planning type
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setDialogOpen(true)}
                >
                  Ajouter un créneau
                </Button>
              </Stack>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
            gap: 3
          }}
        >
          {disponibilitesParJour.map((jour) => (
            <Card key={jour.value}>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  {jour.label}
                </Typography>
                
                <Stack spacing={1} mb={2}>
                  {jour.disponibilites.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" fontStyle="italic">
                      Aucun créneau défini
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {jour.disponibilites.map((dispo) => (
                        <Box
                          key={dispo.id}
                          sx={{
                            p: 1,
                            bgcolor: dispo.actif ? 'success.light' : 'action.hover',
                            borderRadius: 1,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <Typography variant="body2" sx={{ opacity: dispo.actif ? 1 : 0.6 }}>
                            {dispo.heure_debut} - {dispo.heure_fin}
                          </Typography>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <FormControlLabel
                              control={
                                <Switch
                                  checked={dispo.actif}
                                  onChange={() => handleToggleActif(dispo)}
                                  size="small"
                                />
                              }
                              label=""
                              sx={{ m: 0 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleEdit(dispo)}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(dispo.id)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Stack>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Stack>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setFormData({
                      jour_semaine: jour.value,
                      heure_debut: null,
                      heure_fin: null,
                      actif: true
                    });
                    setEditingDispo(null);
                    setDialogOpen(true);
                  }}
                  fullWidth
                >
                  Ajouter un créneau
                </Button>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Dialog pour ajouter/modifier une disponibilité */}
        <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingDispo ? 'Modifier la disponibilité' : 'Ajouter une disponibilité'}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Jour de la semaine</InputLabel>
                <Select
                  value={formData.jour_semaine}
                  label="Jour de la semaine"
                  onChange={(e) => setFormData(prev => ({ ...prev, jour_semaine: e.target.value as number }))}
                >
                  {joursMapping.map((jour) => (
                    <MenuItem key={jour.value} value={jour.value}>
                      {jour.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                type="time"
                label="Heure de début"
                value={formData.heure_debut ? formData.heure_debut.toTimeString().slice(0,5) : ''}
                onChange={(e) => {
                  const time = e.target.value;
                  if (time) {
                    const date = new Date();
                    const [hours, minutes] = time.split(':');
                    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                    setFormData(prev => ({ ...prev, heure_debut: date }));
                  } else {
                    setFormData(prev => ({ ...prev, heure_debut: null }));
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                type="time"
                label="Heure de fin"
                value={formData.heure_fin ? formData.heure_fin.toTimeString().slice(0,5) : ''}
                onChange={(e) => {
                  const time = e.target.value;
                  if (time) {
                    const date = new Date();
                    const [hours, minutes] = time.split(':');
                    date.setHours(parseInt(hours), parseInt(minutes), 0, 0);
                    setFormData(prev => ({ ...prev, heure_fin: date }));
                  } else {
                    setFormData(prev => ({ ...prev, heure_fin: null }));
                  }
                }}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={formData.actif}
                    onChange={(e) => setFormData(prev => ({ ...prev, actif: e.target.checked }))}
                  />
                }
                label="Actif"
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              Annuler
            </Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingDispo ? 'Modifier' : 'Ajouter'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog pour le planning type */}
        <Dialog open={planningTypeDialog} onClose={() => setPlanningTypeDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>
            Définir un planning type
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Définissez vos plages horaires et la durée des RDV. Les créneaux seront générés automatiquement (ex: 9h-12h avec 30 min = 6 créneaux). Le dernier créneau n'est pas créé s'il reste moins de 5 min.
            </Typography>
            
            <Stack spacing={3}>
              {joursMapping.map((jour) => (
                <Box key={jour.key}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6">{jour.label}</Typography>
                    <Button
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addCreneauToPlanningType(jour.key)}
                    >
                      Ajouter créneau
                    </Button>
                  </Box>
                  
                  {planningType[jour.key].length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      Jour de repos
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {planningType[jour.key].map((creneau, index) => (
                        <Box key={index} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                          <TextField
                            type="time"
                            label="Début"
                            value={creneau.debut}
                            onChange={(e) => handlePlanningTypeChange(jour.key, index, 'debut', e.target.value)}
                            size="small"
                          />
                          <TextField
                            type="time"
                            label="Fin"
                            value={creneau.fin}
                            onChange={(e) => handlePlanningTypeChange(jour.key, index, 'fin', e.target.value)}
                            size="small"
                          />
                          <FormControl size="small" sx={{ minWidth: 120 }}>
                            <InputLabel>Durée RDV</InputLabel>
                            <Select
                              value={creneau.duree_minutes ?? 30}
                              label="Durée RDV"
                              onChange={(e) => handlePlanningTypeChange(jour.key, index, 'duree_minutes', Number(e.target.value))}
                            >
                              <MenuItem value={15}>15 min</MenuItem>
                              <MenuItem value={30}>30 min</MenuItem>
                              <MenuItem value={45}>45 min</MenuItem>
                              <MenuItem value={60}>60 min</MenuItem>
                            </Select>
                          </FormControl>
                          <IconButton
                            onClick={() => removeCreneauFromPlanningType(jour.key, index)}
                            color="error"
                            size="small"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                    </Stack>
                  )}
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPlanningTypeDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSavePlanningType} variant="contained">
              Sauvegarder le planning
            </Button>
          </DialogActions>
        </Dialog>
          </>
        )}

        {activeTab === 1 && (
          <GestionIndisponibilites />
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default GestionDisponibilites; 